import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@monksflow/monks-ui';
import { useTranslation } from 'react-i18next';

import { CITATION_PILL_CLASS } from '../constants';
import type { NumberedLink } from '../utils/markdownLinks';
import { InlineCitationStatic } from './InlineCitationStatic';
import { useOpenSource } from './SourcePanel';
import { sourceFromLink } from './SourcePanel/toSource';

interface InlineCitationProps {
  link: NumberedLink;
}

/**
 * The citation number in the prose, opening the source drawer beside the text so a reader can
 * check provenance without losing their place.
 *
 * Not a `Badge`: Badge wraps itself in a controlled tooltip that races this one, and hovering
 * flickers. It also supplied the `TooltipProvider` Radix throws without, hence the one here.
 *
 * With no provider there is no drawer to open, so the number links to its source instead.
 */
export const InlineCitation = ({ link }: InlineCitationProps) => {
  const { t } = useTranslation('analysis');
  const openSource = useOpenSource();

  if (!openSource) return <InlineCitationStatic link={link} />;

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            data-testid="inline-citation"
            aria-label={t('analysis.sourcePanel.viewSourceNumber', { number: link.number })}
            onClick={(event) => openSource(sourceFromLink(link), event.currentTarget)}
            className={`${CITATION_PILL_CLASS} cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring`}
          >
            {link.number}
          </button>
        </TooltipTrigger>
        <TooltipContent side="bottom">{t('analysis.sourcePanel.viewSource')}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};
